import type { FieldValues } from 'react-hook-form';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import ReCAPTCHAEnterprise from 'react-google-recaptcha-enterprise';
import { Controller } from 'react-hook-form';

import { getCaptchaTheme } from '@/utils/theme/themeEngine';

import type { CaptchaResponse, CaptchaWidgetProps } from '../index';

const RECAPTCHA_V2_PROVIDER = 'recaptcha_v2';
const RECAPTCHA_ENTERPRISE_PROVIDER = 'recaptcha_enterprise';

// Type alias for the component prop to be rendered
type ReCAPTCHAComponent = typeof ReCAPTCHA | typeof ReCAPTCHAEnterprise;

const RecaptchaCombinedWidget = <T extends FieldValues = FieldValues>({
  config,
  onCaptchaResponse,
  control,
  name,
  rules,
  setError,
  clearErrors,
  error,
  theme,
}: CaptchaWidgetProps<T>) => {
  // Sync external Auth0 server error into react-hook-form
  useEffect(() => {
    if (error && setError) {
      setError(name, { type: 'server', message: error });
    }
  }, [error, name, setError]);

  // --- Guards ---
  const isEnterprise = config.provider === RECAPTCHA_ENTERPRISE_PROVIDER;
  const isV2 = config.provider === RECAPTCHA_V2_PROVIDER;

  if (!isEnterprise && !isV2) {
    return null;
  }

  const siteKey = config.siteKey;
  if (!siteKey) {
    return null;
  }

  const ReCAPTCHAComponentToRender: ReCAPTCHAComponent = isEnterprise
    ? ReCAPTCHAEnterprise
    : ReCAPTCHA;

  const handleChange = (
    value: string | null,
    onChange: (value: string) => void,
  ) => {
    const token = value || '';
    onChange(token);

    if (value) {
      clearErrors?.(name);
      const response: CaptchaResponse = {
        provider: config.provider,
        token: value,
      };
      onCaptchaResponse(response);
    } else {
      onCaptchaResponse(null);
    }
  };

  const handleExpired = (onChange: (value: string) => void) => {
    onChange('');
    onCaptchaResponse(null);
    clearErrors?.(name);
  };

  const handleError = (onChange: (value: string) => void) => {
    onChange('');
    onCaptchaResponse(null);
  };

  const recaptchaWidgetStyle = {
    transformOrigin: '0 0',
    overflow: 'hidden',
    display: 'inline-block',
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange }, fieldState }) => (
        <div>
          <div style={recaptchaWidgetStyle}>
            <ReCAPTCHAComponentToRender
              sitekey={siteKey}
              onChange={(value) => handleChange(value, onChange)}
              onExpired={() => handleExpired(onChange)}
              onError={() => handleError(onChange)}
              theme={getCaptchaTheme(theme)}
              size="normal"
            />
          </div>
          {fieldState.error?.message && (
            <StatusMessage className="u-margin-t-xs" mode="error">
              {fieldState.error.message}
            </StatusMessage>
          )}
        </div>
      )}
    />
  );
};

export default RecaptchaCombinedWidget;
